import type { ErroreApi } from '@aulaviva/shared';
import { comuni } from '@/testi';

export const API_BASE = '/api/v1';

/** Errore restituito dall'API nel formato comune `{ codice, messaggio, dettagli? }`. */
export class ErroreRichiesta extends Error {
  constructor(
    public readonly status: number,
    public readonly codice: string,
    messaggio: string,
    public readonly dettagli?: Record<string, string[]>,
  ) {
    super(messaggio);
    this.name = 'ErroreRichiesta';
  }
}

function isErroreApi(v: unknown): v is ErroreApi {
  return typeof v === 'object' && v !== null && 'codice' in v && 'messaggio' in v;
}

export interface RispostaApi<T> {
  status: number;
  dati: T;
}

/**
 * Chiamata all'API con cookie di sessione. Lancia `ErroreRichiesta` per 4xx/5xx
 * e per problemi di rete (codice `rete`).
 */
export async function richiesta<T>(percorso: string, init: RequestInit & { json?: unknown } = {}): Promise<RispostaApi<T>> {
  const { json, headers, ...resto } = init;
  let risposta: Response;
  try {
    risposta = await fetch(`${API_BASE}${percorso}`, {
      credentials: 'include',
      headers: { Accept: 'application/json', ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}), ...headers },
      body: json !== undefined ? JSON.stringify(json) : (resto.body ?? null),
      ...resto,
    });
  } catch {
    throw new ErroreRichiesta(0, 'rete', comuni.erroreRete);
  }

  const testo = await risposta.text();
  let corpo: unknown = null;
  if (testo) {
    try {
      corpo = JSON.parse(testo);
    } catch {
      corpo = null;
    }
  }

  if (!risposta.ok) {
    // Amministratore senza secondo fattore: l'area admin è bloccata finché non lo attiva.
    if (risposta.status === 403 && isErroreApi(corpo) && corpo.codice === 'totp_richiesto' && typeof window !== 'undefined' && !window.location.pathname.startsWith('/sicurezza')) {
      window.location.assign('/sicurezza?motivo=totp');
    }
    if (isErroreApi(corpo)) throw new ErroreRichiesta(risposta.status, corpo.codice, corpo.messaggio, corpo.dettagli);
    throw new ErroreRichiesta(risposta.status, 'sconosciuto', comuni.erroreGenerico);
  }
  return { status: risposta.status, dati: corpo as T };
}
